import { Component, EventEmitter, Input, Output } from '@angular/core';
import { AlertType, SweetAlertService } from '../../Utilities/sweet-alert.service';

export type FileExtension =
  // Images
  | '.jpg' | '.jpeg' | '.png' | '.gif' | '.webp' | '.svg' | '.bmp' | '.tiff'
  // Documents
  | '.pdf' | '.doc' | '.docx' | '.txt' | '.rtf'
  // Spreadsheets
  | '.xls' | '.xlsx' | '.csv' | '.ods'
  // Archives
  | '.zip' | '.rar' | '.7z' | '.tar' | '.gz'
  // Audio / Video
  | '.mp3' | '.mp4' | '.wav' | '.avi' | '.mov' | '.mkv'
  // Code / Data
  | '.json' | '.xml' | '.yaml' | '.yml';

@Component({
  selector: 'app-drag-and-drop-file',
  templateUrl: './drag-and-drop-file.component.html',
  styleUrls: ['./drag-and-drop-file.component.scss']
})
export class DragAndDropFileComponent {

  @Input() subtitle: string;
  /** List of file extensions to accept, e.g. ['.csv'], ['.jpg', '.png'], ['.xlsx', '.csv']. */
  @Input() allowedExtensions: FileExtension[] = [];
  @Output() fileSelected = new EventEmitter<File>();
  selectedFile: File;
  fileName: string;
  isDragOver: boolean = false;

  constructor (
    private sweetalertService: SweetAlertService
  ) { }

  /** The value for the native <input accept> attribute. */
  get acceptAttr(): string {
    return this.allowedExtensions?.length ? this.allowedExtensions.join(',') : '*';
  }

  /** Human-readable label shown in error messages and subtitles. */
  get allowedLabel(): string {
    return this.allowedExtensions?.length
      ? this.allowedExtensions.map(e => e.replace('.', '').toUpperCase()).join(', ')
      : 'any';
  }

  onFileDropped(event: any) {
    event.preventDefault();
    const target = event.dataTransfer;
    this.handleFile(target);
    this.isDragOver = false;
  }
  
  onFileSelected(event: Event) {
    const target = event.target;
    this.handleFile(target);
    this.isDragOver = false;
  }

  handleFile(target: any) {
    if (!target.files || target.files.length !== 1) return;

    const file = target.files[0];

    if (this.isSupportedFile(file)) {
      this.selectedFile = file;
      this.fileName = file.name;
      this.fileSelected.emit(file);
    } else {
      this.clearFileInput();
      this.sweetalertService.showAlert(
        `Invalid file type. Only ${this.allowedLabel} files are allowed.`,
        AlertType.Warning
      );
    }
  }
  
  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = true;
  }

  isSupportedFile(file: File): boolean {
    if (!this.allowedExtensions?.length) return true;
    const fileName = file.name.toLowerCase();
    return this.allowedExtensions.some(ext => fileName.endsWith(ext.toLowerCase()));
  }
  
  clearFileInput() {
    const fileInput: HTMLInputElement | null = document.querySelector('input[type="file"]');
    if (fileInput) {
      fileInput.value = '';
    }
  }
}